import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import MemberDetail from './MemberDetail';
import ConfirmationModal from './ConfirmationModal';
import { hasAdminRights } from '../utils/roleUtils';
import {
  MagnifyingGlassIcon,
  UserPlusIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserIcon,
  ExclamationCircleIcon,
  ChevronRightIcon,
  XMarkIcon,
  CalendarIcon,
  MapPinIcon,
  ShieldCheckIcon,
  CloudArrowUpIcon,
  TicketIcon,
  ClockIcon,
  PlusIcon,
  HomeIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { FaDog } from 'react-icons/fa';
import MembersCSVImporter from './MembersCSVImporter';
import AuditHistoryModal from './AuditHistoryModal';
import MemberExcelExportModal from './MemberExcelExportModal';

const CARD_BG = '#1c2432';

// Type-safe wrapper for FaDog icon
const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const IconComponent = FaDog as React.ComponentType<{ className?: string }>;
  return <IconComponent className={className} />;
};

interface Member {
  mg_nr: string;
  mitglied_nr: string;
  name: string;
  vorname: string;
  email: string;
  tel_nummer: string;
  strasse: string;
  plz: string;
  ort: string;
  geb_am: string;
  eintritt: string;
  vorstand: number;
  trainer: number;
  active: number;
  aktiv?: number; // For compatibility with MemberDetail modal
  last_login?: string;
  foto_path?: string;
  foto_path_updated?: string;
  gender?: string;
  datenschutzerklaerung?: number;
  geloescht?: number;
  geloescht_am?: string;
  roles?: Array<{
    id: number;
    name: string;
    description: string;
    priority: number;
    created_at: string;
  }>;
  primary_role?: {
    id: number;
    name: string;
    description: string;
    priority: number;
    created_at: string;
  };
}

interface DogStatus {
  hasDogs: boolean;
  dogsPaid: boolean;
  count: number;
  dogs?: Array<{
    name: string;
    id?: string;
    valid_insurance?: boolean;
    valid_tollwut?: boolean;
  }>;
}

interface MemberPayment {
  amount: number;
  last_payment_date: string | null;
  possible_member_type: string;
  membership_contributions: any;
}

const Members: React.FC = () => {
  const { user, BASE_URL } = useContext(AuthContext);
  const [members, setMembers] = useState<Member[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<Member[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  // Mobile-Pagination: auf <md zeigen wir initial 25 und laden auf Klick nach
  const [isMobile, setIsMobile] = useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [mobileVisible, setMobileVisible] = useState(25);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);
  // Reset Mobile-Pagination wenn Filter / Suche sich ändern
  useEffect(() => { setMobileVisible(25); }, [filteredMembers.length]);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dogsStatus, setDogsStatus] = useState<Record<string, DogStatus>>({});
  const [memberPayments, setMemberPayments] = useState<Record<string, MemberPayment>>({});
  const [guestCards, setGuestCards] = useState<Record<string, { count: number; remaining: number }>>({});
  const [profileImages, setProfileImages] = useState<Record<string, string>>({});
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [filterType, setFilterType] = useState<'all' | 'active' | 'inactive' | 'board' | 'trainer' | 'members_only' | 'guests_only' | 'deleted'>('all');
  const [sortField, setSortField] = useState<'name' | 'status' | 'guthaben' | 'lastLogin'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState<number[]>([new Date().getFullYear()]);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'success' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
    onCancel: () => {}
  });
  const [showNewMemberModal, setShowNewMemberModal] = useState(false);
  const [newMemberData, setNewMemberData] = useState({
    vorname: '',
    name: '',
    email: '',
    tel_nummer: '',
    strasse: '',
    plz: '',
    ort: '',
    geb_am: '',
    gender: '',
    eintritt: '',
    active: 1,
    role_ids: [1] // Default: Guest role (id: 5)
  });
  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const [showCSVImporter, setShowCSVImporter] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [showExcelExportModal, setShowExcelExportModal] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const searchInputRef = useRef<HTMLInputElement>(null);

  const hasAccess = hasAdminRights(user);

  const currentYear = new Date().getFullYear();
  const isPastYear = selectedYear < currentYear;

  // Memoized filter counts for performance
  const filterCounts = useMemo(() => ({
    all: members.length,
    members: members.filter(m => m.roles?.some(role => role.name === 'member')).length,
    guests: members.filter(m => m.roles?.length === 1 && m.roles[0].name === 'guest').length,
    inactive: members.filter(m => m.active === 0).length,
    active: members.filter(m => m.active === 1).length,
    deleted: members.filter(m => m.geloescht === 1).length,
    board: members.filter(m => m.roles?.some(role => role.name === 'board')).length,
    trainer: members.filter(m => m.roles?.some(role => role.name === 'trainer')).length,
  }), [members]);

  // Calculate "open Beitrag" count - members with payment 0 or negative (excluding guests)
  const beitragStats = useMemo(() => {
    const memberOnly = members.filter(m =>
      m.active === 1 &&
      m.roles?.some(role => role.name === 'member')
    );
    let open = 0;
    let openAmount = 0;
    let paid = 0;
    memberOnly.forEach(m => {
      const p = memberPayments[m.mg_nr];
      if (!p || p.amount <= 0) {
        open += 1;
        openAmount += 120;
      } else {
        paid += 1;
      }
    });
    const total = memberOnly.length;
    const quote = total > 0 ? Math.round((paid / total) * 100) : 0;
    return { open, openAmount, paid, total, quote };
  }, [members, memberPayments]);

  // Auto-focus search input when expanded
  useEffect(() => {
    if (searchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchExpanded]);

  // Close search on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && searchExpanded) {
        setSearchExpanded(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [searchExpanded]);

  // Fetch available roles
  useEffect(() => {
    const fetchAvailableRoles = async () => {
      try {
        const response = await apiClient.get('/api/roles/available');
        if (response.data.success && Array.isArray(response.data.roles)) {
          setAvailableRoles(response.data.roles);
        }
      } catch (error) {
        console.error('Error fetching available roles:', error);
      }
    };
    if (hasAccess) {
      fetchAvailableRoles();
    }
  }, [hasAccess]);

  const handleCreateMember = async () => {
    try {
      const response = await apiClient.post('/api/members', newMemberData);
      if (response.data.success) {
        setShowNewMemberModal(false);
        setNewMemberData({
          vorname: '',
          name: '',
          email: '',
          tel_nummer: '',
          strasse: '',
          plz: '',
          ort: '',
          geb_am: '',
          gender: '',
          eintritt: '',
          active: 1,
          role_ids: [1] // Reset to default Guest role
        });
        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Neues Mitglied wurde erfolgreich hinzugefügt!',
          variant: 'single',
          onConfirm: () => {
            setConfirmModal(prev => ({ ...prev, isOpen: false }));
          },
          onCancel: () => {
            setConfirmModal(prev => ({ ...prev, isOpen: false }));
          }
        });
        fetchMembers(); // Refresh member list
      }
    } catch (error: any) {
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: error.response?.data?.message || 'Fehler beim Hinzufügen des Mitglieds',
        variant: 'single',
        onConfirm: () => {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        },
        onCancel: () => {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      });
    }
  };

  const fetchAvailableYears = async () => {
    try {
      const response = await apiClient.get('/api/members/available-years');
      if (response.data.success) {
        setAvailableYears(response.data.years);
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Error fetching available years:', err);
      }
    }
  };

  useEffect(() => {
    if (hasAccess) {
      fetchAvailableYears();
      fetchMembers();
      fetchDogsStatus();
      fetchMemberPayments();
      fetchGuestCards();
    }
  }, [hasAccess]);

  // Re-fetch when year changes
  useEffect(() => {
    if (hasAccess) {
      fetchMembers();
      fetchMemberPayments();
    }
  }, [selectedYear]);

  useEffect(() => {
    filterMembers();
  }, [searchTerm, filterType, members, sortField, sortDir, memberPayments]);

  const toggleSort = (field: typeof sortField) => {
    if (field === sortField) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const url = selectedYear < currentYear
        ? `/api/members?year=${selectedYear}`
        : '/api/members';
      const response = await apiClient.get(url);
      const data = response.data;
      setMembers(data);
      setFilteredMembers(data);

      // Fetch profile images for each member
      data.forEach((member: Member) => {
        if (member.foto_path && member.foto_path_updated) {
          fetchProfileImage(member.mg_nr, member.foto_path_updated);
        }
      });
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Error fetching members:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDogsStatus = async () => {
    try {
      const response = await apiClient.get('/api/members/all_dogs_status');
      const data = response.data;
      if (data.success) {
        const statusMap: Record<string, DogStatus> = {};
        data.data.members.forEach((member: any) => {
          const hasDogs = member.dogs.length > 0;
          const dogsPaid = hasDogs ? member.dogs.every((dog: any) => dog.valid_insurance && dog.valid_tollwut) : false;
          const count = member.dogs.length;
          const dogData = {
            hasDogs,
            dogsPaid,
            count,
            dogs: member.dogs.map((dog: any) => ({
              name: dog.hundename || 'Unbekannt',
              id: dog.dog_id || dog.id,
              valid_insurance: dog.valid_insurance,
              valid_tollwut: dog.valid_tollwut
            }))
          };
          // Map only by mg_nr
          statusMap[String(member.member_nr)] = dogData;
        });
        setDogsStatus(statusMap);
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Error fetching dogs status:', err);
      }
    }
  };

  const fetchMemberPayments = async () => {
    try {
      const response = await apiClient.get(`/api/members/payments?currentYear=${selectedYear}`);
      const data = response.data;
      const paymentsMap: Record<string, MemberPayment> = {};

      data.forEach((member: any) => {
        const yearPayment = member.payments.find((p: any) => p.year === selectedYear);
        if (yearPayment) {
          paymentsMap[String(member.member_nr)] = {
            amount: yearPayment.amount || 0,
            last_payment_date: yearPayment.last_payment_date || null,
            possible_member_type: yearPayment.possible_member_type || 'unbekannt',
            membership_contributions: yearPayment.membership_contributions || {}
          };
        } else {
          paymentsMap[String(member.member_nr)] = {
            amount: 0,
            last_payment_date: null,
            possible_member_type: 'keine_zahlung',
            membership_contributions: {}
          };
        }
      });

      setMemberPayments(paymentsMap);
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Error fetching payments:', err);
      }
    }
  };

  const fetchGuestCards = async () => {
    try {
      const response = await apiClient.get('/api/guest-cards/guests-with-cards');
      if (response.data.success) {
        const cardsMap: Record<string, { count: number; remaining: number }> = {};
        response.data.guests.forEach((guest: any) => {
          cardsMap[String(guest.mg_nr)] = {
            count: guest.cards?.length || 0,
            remaining: guest.cards?.reduce((sum: number, card: any) => sum + (card.remaining_entries || 0), 0) || 0
          };
        });
        setGuestCards(cardsMap);
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        console.error('Error fetching guest cards:', err);
      }
    }
  };

  const fetchProfileImage = async (mg_nr: string, foto_path_updated?: string) => {
    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) return;

      // Use foto_path_updated as timestamp if available, otherwise use current time
      const timestamp = foto_path_updated || new Date().getTime();
      const imageUrl = `${BASE_URL}/api/members/user-image-url/${mg_nr}?size=S&token=${token}&t=${timestamp}`;
      setProfileImages(prev => ({ ...prev, [mg_nr]: imageUrl }));
    } catch (err) {
      console.error(`Error fetching profile image for ${mg_nr}:`, err);
    }
  };

  const filterMembers = () => {
    let filtered = [...members];

    // Apply search filter - erweitert um PLZ, Straße, Ort, Eintrittsdatum
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(member => {
        // Name und Vorname
        if (member.name?.toLowerCase().includes(searchLower)) return true;
        if (member.vorname?.toLowerCase().includes(searchLower)) return true;

        // Kontaktdaten
        if (member.email?.toLowerCase().includes(searchLower)) return true;
        if (member.mitglied_nr?.toLowerCase().includes(searchLower)) return true;

        // Adresse
        if (member.plz?.toLowerCase().includes(searchLower)) return true;
        if (member.strasse?.toLowerCase().includes(searchLower)) return true;
        if (member.ort?.toLowerCase().includes(searchLower)) return true;

        // Eintrittsdatum (Format: YYYY-MM-DD oder DD.MM.YYYY für Suche)
        if (member.eintritt) {
          const eintrittFormatted = member.eintritt.replace(/-/g, '.');
          if (eintrittFormatted.includes(searchTerm) || member.eintritt.includes(searchTerm)) return true;
        }

        return false;
      });
    }

    // Apply type filter
    switch (filterType) {
      case 'active':
        filtered = filtered.filter(m => m.active === 1);
        break;
      case 'inactive':
        filtered = filtered.filter(m => m.active === 0);
        break;
      case 'board':
        filtered = filtered.filter(m => m.roles?.some(role => role.name === 'board'));
        break;
      case 'trainer':
        filtered = filtered.filter(m => m.roles?.some(role => role.name === 'trainer'));
        break;
      case 'members_only':
        // Nur Mitglieder (keine Gäste) - basierend auf Rollen
        filtered = filtered.filter(m => {
          if (!m.roles || m.roles.length === 0) return false;
          // Prüfe ob mindestens eine Rolle NICHT 'guest' ist
          return m.roles.some((role: any) => role.name !== 'guest');
        });
        break;
      case 'guests_only':
        // Nur Gäste - basierend auf Rollen
        filtered = filtered.filter(m => {
          if (!m.roles || m.roles.length === 0) return false;
          // Prüfe ob NUR die 'guest' Rolle vorhanden ist
          return m.roles.length === 1 && m.roles[0].name === 'guest';
        });
        break;
      case 'deleted':
        filtered = filtered.filter(m => m.geloescht === 1);
        break;
    }

    // Sorting
    const dir = sortDir === 'asc' ? 1 : -1;
    const rolePrio: Record<string, number> = { board: 5, trainer: 4, auditor: 3, member: 2, guest: 1 };
    filtered.sort((a, b) => {
      switch (sortField) {
        case 'name': {
          const an = `${a.name || ''} ${a.vorname || ''}`.toLowerCase();
          const bn = `${b.name || ''} ${b.vorname || ''}`.toLowerCase();
          return an.localeCompare(bn, 'de') * dir;
        }
        case 'status': {
          const aName = a.roles?.sort((x: any, y: any) => y.priority - x.priority)[0]?.name || '';
          const bName = b.roles?.sort((x: any, y: any) => y.priority - x.priority)[0]?.name || '';
          const ar = a.active === 1 ? (rolePrio[aName] || 0) : -1;
          const br = b.active === 1 ? (rolePrio[bName] || 0) : -1;
          return (br - ar) * dir;
        }
        case 'guthaben': {
          const av = memberPayments[a.mg_nr]?.amount ?? 0;
          const bv = memberPayments[b.mg_nr]?.amount ?? 0;
          return (av - bv) * dir;
        }
        case 'lastLogin': {
          const at = a.last_login ? new Date(a.last_login).getTime() : 0;
          const bt = b.last_login ? new Date(b.last_login).getTime() : 0;
          return (bt - at) * dir;
        }
      }
      return 0;
    });

    setFilteredMembers(filtered);
  };

  const handleViewDetails = (member: Member) => {
    setSelectedMember(member);
    setShowDetailsModal(true);
  };

  const handleMemberSave = async (updatedMember: Member) => {
    try {
      const response = await apiClient.put(`/api/members/${updatedMember.mg_nr}`, updatedMember);
      if (response.data.success) {
        // Update local state
        setMembers(prev => prev.map(m => m.mg_nr === updatedMember.mg_nr ? updatedMember : m));

        // Show success feedback
        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Mitgliederdaten wurden erfolgreich aktualisiert.',
          variant: 'single',
          onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
          onCancel: () => {}
        });

        return { success: true };
      }

      // Show error feedback
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: 'Die Mitgliederdaten konnten nicht aktualisiert werden.',
        variant: 'single',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
        onCancel: () => {}
      });

      return { success: false, error: 'Update failed' };
    } catch (error: any) {
      console.error('Error updating member:', error);

      // Show error feedback
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: `Fehler beim Aktualisieren: ${error.message}`,
        variant: 'single',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
        onCancel: () => {}
      });

      return { success: false, error: error.message };
    }
  };

  const handleMemberDelete = async (memberId: string) => {
    try {
      // Delete member via API
      const response = await apiClient.delete(`/api/members/${memberId}`);

      if (response.data.success) {
        // Update local state - remove deleted member
        setMembers(prevMembers =>
          prevMembers.filter(m => m.mg_nr !== memberId)
        );

        // Close the detail modal
        setShowDetailsModal(false);
        setSelectedMember(null);

        // Show success feedback
        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Erfolgreich',
          message: 'Mitglied wurde erfolgreich gelöscht.',
          variant: 'single',
          onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
          onCancel: () => {}
        });
      } else {
        // Show error feedback
        setConfirmModal({
          isOpen: true,
          type: 'error',
          title: 'Fehler',
          message: 'Das Mitglied konnte nicht gelöscht werden.',
          variant: 'single',
          onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
          onCancel: () => {}
        });
      }
    } catch (error: any) {
      console.error('Error deleting member:', error);

      // Show error feedback
      setConfirmModal({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: `Fehler beim Löschen: ${error.message}`,
        variant: 'single',
        onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
        onCancel: () => {}
      });
    }
  };

  // Helper: get initials from member
  const getInitials = (m: Member) => {
    const v = (m.vorname || '').trim();
    const n = (m.name || '').trim();
    return `${v.charAt(0)}${n.charAt(0)}`.toUpperCase() || '?';
  };

  // Helper: avatar role classes (theme-aware, Tailwind)
  const getAvatarRoleClass = (m: Member) => {
    // className covers bg, border and text — works in light and dark mode
    if (m.active !== 1) {
      return { className: 'bg-slate-200 dark:bg-slate-500/10 border border-slate-400/40 dark:border-slate-500/30 text-slate-600 dark:text-slate-400' };
    }
    const pr = m.roles?.sort((a: any, b: any) => b.priority - a.priority)[0];
    if (!pr) {
      return { className: 'bg-slate-100 dark:bg-slate-500/10 border border-slate-300 dark:border-slate-500/30 text-slate-700 dark:text-slate-300' };
    }
    switch (pr.name) {
      case 'board':
        return { className: 'bg-amber-100 dark:bg-amber-500/15 border border-amber-400 dark:border-amber-500/40 text-amber-700 dark:text-amber-300' };
      case 'trainer':
        return { className: 'bg-blue-100 dark:bg-blue-500/15 border border-blue-400 dark:border-blue-500/40 text-blue-700 dark:text-blue-300' };
      case 'member':
        return { className: 'bg-emerald-100 dark:bg-emerald-500/15 border border-emerald-400 dark:border-emerald-500/40 text-emerald-700 dark:text-emerald-300' };
      case 'auditor':
        return { className: 'bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/30 text-amber-700 dark:text-amber-200' };
      case 'guest':
        return { className: 'bg-slate-100 dark:bg-slate-500/10 border border-slate-300 dark:border-slate-500/30 text-slate-700 dark:text-slate-300' };
      default:
        return { className: 'bg-slate-100 dark:bg-slate-500/10 border border-slate-300 dark:border-slate-500/30 text-slate-700 dark:text-slate-300' };
    }
  };

  // Helper: format last-login info
  const formatLastLogin = (lastLogin?: string) => {
    if (!lastLogin) return { label: 'nie', color: 'text-slate-500' };
    const d = new Date(lastLogin);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return { label: 'heute', color: 'text-primary-300' };
    if (diffDays === 1) return { label: 'gestern', color: 'text-primary-300' };
    if (diffDays <= 7) return { label: `vor ${diffDays} Tagen`, color: 'text-primary-300' };
    if (diffDays <= 30) return { label: `vor ${diffDays} Tagen`, color: 'text-amber-300' };
    if (diffDays <= 90) return { label: `vor ${Math.floor(diffDays / 30)} Mon.`, color: 'text-amber-300' };
    return { label: d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }), color: 'text-slate-500' };
  };

  // Layout-only pill style (no colors) for theme-aware classNames
  const pillLayout = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 9999,
    fontSize: 11,
    fontWeight: 600,
  };

  // Selection toggling
  const toggleSelect = (mg_nr: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(mg_nr)) next.delete(mg_nr);
      else next.add(mg_nr);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectedCount = selectedIds.size;
  const selectedMembersList = members.filter(m => selectedIds.has(m.mg_nr));

  // Calc derived years for monthYear-style copy
  const monthYearLabel = new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <ExclamationCircleIcon className="h-12 w-12 text-gray-500 dark:text-slate-500 mx-auto mb-4" />
          <p className="text-lg text-gray-600 dark:text-slate-400">
            Zugriff nur für Vorstand und Trainer
          </p>
        </div>
      </div>
    );
  }

  // Compute role-based active counts on the fly
  const newThisMonth = members.filter(m => {
    if (!m.eintritt) return false;
    const ein = new Date(m.eintritt);
    const now = new Date();
    return ein.getFullYear() === now.getFullYear() && ein.getMonth() === now.getMonth();
  }).length;

  // Tab definitions: members / guests / inactive / deleted
  const tabs: Array<{ key: typeof filterType; label: string; count: number }> = [
    { key: 'all', label: 'Alle', count: filterCounts.all },
    { key: 'members_only', label: 'Mitglieder', count: filterCounts.members },
    { key: 'guests_only', label: 'Gäste', count: filterCounts.guests },
    { key: 'inactive', label: 'Inaktive', count: filterCounts.inactive },
    ...(isPastYear && filterCounts.deleted > 0
      ? [{ key: 'deleted' as typeof filterType, label: 'Gelöscht', count: filterCounts.deleted }]
      : []),
  ];

  const filterPills: Array<{ key: typeof filterType; label: string; count: number; tone: string }> = [
    { key: 'all', label: 'Alle', count: filterCounts.all, tone: 'primary' },
    { key: 'active', label: 'Aktiv', count: filterCounts.active, tone: 'neutral' },
    { key: 'inactive', label: 'Inaktiv', count: filterCounts.inactive, tone: 'neutral' },
    { key: 'board', label: 'Vorstand', count: filterCounts.board, tone: 'amber' },
    { key: 'trainer', label: 'Trainer', count: filterCounts.trainer, tone: 'blue' },
  ];

  const filterPillClass = (active: boolean, tone: string) => {
    if (active) {
      switch (tone) {
        case 'primary':
          return 'bg-primary-500 text-slate-950 border border-primary-500';
        case 'amber':
          return 'bg-amber-500 text-slate-950 border border-amber-500';
        case 'blue':
          return 'bg-blue-500 text-slate-950 border border-blue-500';
        default:
          return 'bg-white/15 text-white border border-white/20';
      }
    }
    switch (tone) {
      case 'amber':
        return 'bg-amber-500/10 border border-amber-500/30 text-amber-300 hover:bg-amber-500/20';
      case 'blue':
        return 'bg-blue-500/10 border border-blue-500/30 text-blue-300 hover:bg-blue-500/20';
      default:
        return 'bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10';
    }
  };

  return (
    <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">

        {/* INTRO */}
        <section className="pt-2 pb-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-amber-400/80 font-semibold mb-3">
            {filterCounts.active} aktive {filterCounts.active === 1 ? 'Person' : 'Personen'} · Stand {monthYearLabel}
          </div>
          <h2 className="text-3xl md:text-5xl font-black tracking-tight text-gray-900 dark:text-white leading-[1.04]" style={{ letterSpacing: '-0.04em' }}>
            Deine<br /><span className="text-amber-600 dark:text-amber-400">Vereinsfamilie.</span>
          </h2>
          <p className="text-gray-600 dark:text-slate-400 text-base md:text-lg leading-relaxed mt-5 max-w-2xl">
            <span className="font-bold text-gray-900 dark:text-white">{filterCounts.all} {filterCounts.all === 1 ? 'Person' : 'Personen'}</span>
            {filterCounts.board > 0 && (
              <> · <span className="font-bold text-amber-700 dark:text-amber-300">{filterCounts.board}</span> im Vorstand</>
            )}
            {filterCounts.trainer > 0 && (
              <> · <span className="font-bold text-blue-700 dark:text-blue-300">{filterCounts.trainer}</span> {filterCounts.trainer === 1 ? 'Trainer' : 'Trainer'}</>
            )}
            {filterCounts.guests > 0 && (
              <> · <span className="font-bold text-gray-800 dark:text-slate-200">{filterCounts.guests}</span> {filterCounts.guests === 1 ? 'Gast' : 'Gäste'}</>
            )}
            {beitragStats.open > 0 && (
              <> · <span className="font-bold text-amber-700 dark:text-amber-300">{beitragStats.open}</span> mit offenem Beitrag</>
            )}
            {newThisMonth > 0 && (
              <> · <span className="font-bold text-primary-700 dark:text-primary-300">{newThisMonth} neu</span> diesen Monat — Willkommen.</>
            )}
            {newThisMonth === 0 && '.'}
          </p>
        </section>

        {/* HERO: ACTION REQUIRED — only when there are open Beiträge */}
        {beitragStats.open > 0 && !isPastYear && (
          <section className="py-4">
            <div className="flex items-end justify-between mb-5">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-amber-700 dark:text-amber-300/90 font-semibold">Aktion erforderlich</div>
                <h3 className="text-xl md:text-2xl font-black tracking-tight text-gray-900 dark:text-white mt-1">
                  {beitragStats.open} {beitragStats.open === 1 ? 'Beitrag' : 'Beiträge'} offen · {beitragStats.openAmount.toLocaleString('de-DE')} € fällig
                </h3>
              </div>
              <span className="hidden md:inline-flex text-amber-700 dark:text-amber-200 border border-amber-500/40 dark:border-amber-500/30 bg-amber-500/10 dark:bg-amber-500/15" style={pillLayout}>
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 dark:bg-amber-400 animate-pulse" /> Mahnstufe 1
              </span>
            </div>

            <article className="relative rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-amber-400 dark:border-amber-500/40 shadow-sm dark:shadow-[0_30px_60px_-20px_rgba(0,0,0,0.55)]">
              <div className="relative px-6 md:px-8 py-6 md:py-7">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="text-amber-700 dark:text-amber-200 border border-amber-500/40 dark:border-amber-500/30 bg-amber-500/10 dark:bg-amber-500/15" style={pillLayout}>
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 dark:bg-amber-400 animate-pulse" /> Beitragsjahr {selectedYear}
                  </span>
                  <span className="text-slate-700 dark:text-slate-300 border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5" style={pillLayout}>{beitragStats.open} {beitragStats.open === 1 ? 'Mitglied' : 'Mitglieder'}</span>
                  <span className="text-slate-700 dark:text-slate-300 border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5" style={pillLayout}>Σ {beitragStats.openAmount.toLocaleString('de-DE')} €</span>
                </div>
                <h4 className="text-gray-900 dark:text-white text-2xl md:text-4xl font-black tracking-tight leading-tight">
                  Zeit für die Erinnerung.
                </h4>
                <p className="text-gray-700 dark:text-slate-300 text-sm md:text-base leading-relaxed mt-3 max-w-xl">
                  <span className="font-bold text-amber-700 dark:text-amber-300">{beitragStats.open} {beitragStats.open === 1 ? 'Mitglied hat' : 'Mitglieder haben'}</span> den Beitrag {selectedYear} noch nicht bezahlt. Filter „Inaktiv" oder Suche nutzen, um sie schnell zu finden.
                </p>
              </div>

              <div className="relative px-6 md:px-8 py-5 md:py-6 border-t border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-[#1c2432]">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-5 md:gap-6 items-center">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Offen</div>
                    <div className="text-sm font-bold text-amber-700 dark:text-amber-300 mt-1 leading-tight">
                      {beitragStats.open} {beitragStats.open === 1 ? 'Mitglied' : 'Mitglieder'}<br />
                      <span className="text-[11px] text-gray-600 dark:text-slate-400 font-medium">Stand heute</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Summe</div>
                    <div className="text-sm font-bold text-gray-900 dark:text-white mt-1 tabular-nums">{beitragStats.openAmount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Bezahlt</div>
                    <div className="text-sm font-bold text-primary-700 dark:text-primary-300 mt-1">{beitragStats.paid} / {beitragStats.total}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Quote</div>
                    <div className="text-sm font-bold text-primary-700 dark:text-primary-300 mt-1">{beitragStats.quote}%</div>
                  </div>
                  <div className="col-span-2 md:col-span-1 flex gap-2">
                    <button
                      onClick={() => setFilterType('inactive')}
                      className="group flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 min-h-[68px] rounded-2xl bg-amber-500/10 hover:bg-amber-500/15 border border-amber-500/30 hover:border-amber-500/50 text-amber-700 dark:text-amber-200 text-xs font-bold active:scale-[0.98] transition"
                    >
                      <ExclamationCircleIcon className="w-4 h-4" />
                      Liste sehen
                    </button>
                  </div>
                </div>
              </div>
            </article>
          </section>
        )}

        {/* HISTORICAL YEAR BANNER */}
        {isPastYear && (
          <div className="my-3 flex items-center gap-2 px-4 py-3 rounded-2xl bg-amber-500/10 dark:bg-amber-500/8 border border-amber-500/40 dark:border-amber-500/30">
            <CalendarIcon className="w-4 h-4 text-amber-700 dark:text-amber-300 flex-shrink-0" />
            <span className="text-sm text-amber-700 dark:text-amber-200">
              Historische Ansicht: Mitgliedschaft im Jahr <strong>{selectedYear}</strong>
            </span>
          </div>
        )}

        {/* TABS + TOOLBAR */}
        <section className="py-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            {/* Tabs */}
            <div className="inline-flex p-1 rounded-2xl bg-white/5 border border-white/10 overflow-x-auto no-scrollbar">
              {tabs.map(tab => {
                const active = filterType === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setFilterType(tab.key)}
                    className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm transition whitespace-nowrap ${
                      active
                        ? 'bg-primary-500 text-slate-950 font-extrabold'
                        : 'text-slate-400 hover:text-white font-semibold'
                    }`}
                  >
                    {tab.label}
                    <span className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-bold ${
                      active ? 'bg-slate-950/30' : 'bg-white/10'
                    }`}>
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => toggleSort('name')}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-700 dark:text-slate-300 hover:bg-white/10 text-xs font-semibold transition"
                title="Sortierung umschalten"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M3 6h18M6 12h12M10 18h4" /></svg>
                Sortierung · {sortField === 'name' ? 'Name' : sortField === 'status' ? 'Status' : sortField === 'guthaben' ? 'Guthaben' : 'Login'} {sortDir === 'asc' ? '↑' : '↓'}
              </button>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-700 dark:text-slate-300 hover:bg-white/10 text-xs font-semibold transition outline-none"
                style={{ colorScheme: 'dark' }}
                title="Jahr"
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>Jahr · {year}</option>
                ))}
              </select>
              <button
                onClick={() => setShowAuditModal(true)}
                className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white/5 border border-white/10 text-gray-700 dark:text-slate-300 hover:bg-white/10 transition"
                title="Audit-Verlauf"
              >
                <ClockIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowCSVImporter(true)}
                className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white/5 border border-white/10 text-gray-700 dark:text-slate-300 hover:bg-white/10 transition"
                title="CSV-Import"
              >
                <CloudArrowUpIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowExcelExportModal(true)}
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-700 dark:text-slate-300 hover:bg-white/10 text-xs font-semibold transition"
                title="Excel-Export"
              >
                <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                Excel-Export
              </button>
              <button
                onClick={() => setShowNewMemberModal(true)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold bg-primary-500 hover:bg-primary-400 text-slate-950 transition"
              >
                <PlusIcon className="w-3.5 h-3.5" strokeWidth={2.5} />
                Mitglied hinzufügen
              </button>
            </div>
          </div>

          {/* SEARCH + FILTER PILLS */}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-slate-500" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Mitglied suchen — Name, Mg-Nr, PLZ, Straße, Ort, Eintritt …"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-10 py-3 rounded-2xl bg-white/5 border border-white/10 text-sm text-gray-900 dark:text-white placeholder-slate-500 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30 transition"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition"
                  aria-label="Suche löschen"
                >
                  <XMarkIcon className="w-3.5 h-3.5 text-gray-700 dark:text-slate-300" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {filterPills.map(pill => (
                <button
                  key={pill.key}
                  onClick={() => setFilterType(pill.key)}
                  className={`px-3 py-2 rounded-xl text-xs font-bold transition ${filterPillClass(filterType === pill.key, pill.tone)}`}
                >
                  {pill.label} · {pill.count}
                </button>
              ))}
              {beitragStats.open > 0 && !isPastYear && (
                <button
                  onClick={() => setFilterType('inactive')}
                  className="px-3 py-2 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/20 text-xs font-bold transition"
                >
                  Beitrag offen · {beitragStats.open}
                </button>
              )}
            </div>
          </div>
        </section>

        {/* MEMBER LIST */}
        <section className="pb-4">
          {/* Header bar */}
          {!loading && filteredMembers.length > 0 && (
            <div className="hidden md:flex items-center gap-3 px-5 py-3 text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold border-b border-gray-200 dark:border-slate-800 mb-2">
              <div className="w-6"></div>
              <div className="w-12"></div>
              <div className="flex-1">Name · Mitglieds-Nr</div>
              <div className="w-44">Rolle</div>
              <div className="w-32">Beitrag {selectedYear}</div>
              <div className="w-28">Eintritt</div>
              <div className="w-20 text-right">Aktion</div>
            </div>
          )}

          {loading ? (
            <div className="rounded-3xl p-10 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-500/20 border-t-primary-400 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-slate-400">Lade Mitglieder…</p>
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="rounded-3xl p-10 md:p-14 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}>
              <div className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5"
                style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}>
                <MagnifyingGlassIcon className="w-10 h-10 text-primary-600 dark:text-primary-400" />
              </div>
              <h4 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">
                {searchTerm ? `Keine Treffer für „${searchTerm}".` : 'Keine Mitglieder gefunden.'}
              </h4>
              <p className="text-gray-600 dark:text-slate-400 text-base mt-3 max-w-md mx-auto">
                {searchTerm || filterType !== 'all'
                  ? 'Probier einen anderen Begriff oder leg ein neues Mitglied an.'
                  : 'Lege das erste Mitglied an, um zu starten.'}
              </p>
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                {(searchTerm || filterType !== 'all') && (
                  <button
                    onClick={() => { setSearchTerm(''); setFilterType('all'); }}
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10 text-sm font-semibold transition"
                  >
                    Filter zurücksetzen
                  </button>
                )}
                <button
                  onClick={() => setShowNewMemberModal(true)}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-primary-500 hover:bg-primary-400 text-slate-950 font-bold text-sm transition"
                >
                  Neues Mitglied anlegen
                  <PlusIcon className="w-4 h-4" strokeWidth={2.5} />
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" style={{ boxShadow: '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 30px 60px -20px rgba(0,0,0,0.55)' }}>
              <div className="divide-y divide-gray-200 dark:divide-slate-800">
                {(isMobile ? filteredMembers.slice(0, mobileVisible) : filteredMembers).map(member => {
                  const dogStatus = dogsStatus[member.mg_nr];
                  const payment = memberPayments[member.mg_nr];
                  const isGuest = member.roles?.length === 1 && member.roles[0].name === 'guest';
                  const displayNr = member.mitglied_nr || member.mg_nr;
                  const memberNr = isGuest ? `G-${displayNr}` : `MG-${displayNr}`;
                  const guestCardData = guestCards[member.mg_nr];
                  const guestCardsCount = isGuest ? (guestCardData?.count || 0) : 0;
                  const guestCardsRemaining = isGuest ? (guestCardData?.remaining || 0) : 0;
                  const isDeleted = member.geloescht === 1;
                  const isInactive = member.active !== 1;
                  const checked = selectedIds.has(member.mg_nr);
                  const av = getAvatarRoleClass(member);
                  const lastLogin = formatLastLogin(member.last_login);

                  // Subline parts
                  const sublineParts: string[] = [];
                  if (member.email) sublineParts.push(member.email);
                  if (member.plz || member.ort) sublineParts.push(`${member.plz || ''} ${member.ort || ''}`.trim());
                  if (dogStatus && dogStatus.count > 0) sublineParts.push(`${dogStatus.count} ${dogStatus.count === 1 ? 'Hund' : 'Hunde'}`);

                  // Eintritt year diff
                  let eintrittYears = '';
                  if (member.eintritt) {
                    try {
                      const ein = new Date(member.eintritt);
                      const yrs = new Date().getFullYear() - ein.getFullYear();
                      eintrittYears = yrs > 0 ? `${yrs} ${yrs === 1 ? 'Jahr' : 'Jahre'}` : 'Heute · neu';
                    } catch { /* ignore */ }
                  }

                  // Role pills
                  const rolePills: { label: string; tone: 'primary' | 'amber' | 'blue' | 'slate' }[] = [];
                  if (isInactive) {
                    rolePills.push({ label: 'Inaktiv', tone: 'slate' });
                  } else if (member.roles && member.roles.length > 0) {
                    const sortedRoles = [...member.roles].sort((a: any, b: any) => b.priority - a.priority);
                    sortedRoles.forEach(r => {
                      let tone: 'primary' | 'amber' | 'blue' | 'slate' = 'slate';
                      if (r.name === 'board') tone = 'amber';
                      else if (r.name === 'trainer') tone = 'blue';
                      else if (r.name === 'member') tone = 'primary';
                      else if (r.name === 'auditor') tone = 'amber';
                      else if (r.name === 'guest') tone = 'slate';
                      rolePills.push({ label: r.description || r.name, tone });
                    });
                  }

                  const rolePillClass = (tone: string) => {
                    switch (tone) {
                      case 'primary':
                        return 'text-emerald-700 dark:text-emerald-300 border border-emerald-500/40 dark:border-emerald-500/30 bg-emerald-500/10 dark:bg-emerald-500/15';
                      case 'amber':
                        return 'text-amber-700 dark:text-amber-300 border border-amber-500/40 dark:border-amber-500/30 bg-amber-500/10 dark:bg-amber-500/15';
                      case 'blue':
                        return 'text-blue-700 dark:text-blue-300 border border-blue-500/40 dark:border-blue-500/30 bg-blue-500/10 dark:bg-blue-500/15';
                      default:
                        return 'text-slate-700 dark:text-slate-300 border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-slate-900/55';
                    }
                  };

                  return (
                    <div
                      key={member.mg_nr}
                      onClick={() => handleViewDetails(member)}
                      className={`px-4 sm:px-5 py-4 flex items-center gap-3 cursor-pointer transition ${
                        isDeleted ? 'opacity-60' : ''
                      } ${checked ? 'bg-primary-500/[0.06]' : 'hover:bg-gray-50 dark:hover:bg-white/[0.025]'}`}
                      style={checked ? { borderLeft: '2px solid rgba(16,185,129,0.45)' } : undefined}
                    >
                      {/* Checkbox */}
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleSelect(member.mg_nr); }}
                        className="shrink-0 flex items-center justify-center transition"
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 6,
                          border: checked ? '1.5px solid #10b981' : '1.5px solid rgba(148,163,184,0.5)',
                          background: checked ? '#10b981' : 'transparent',
                        }}
                        aria-label={checked ? 'Auswahl aufheben' : 'Auswählen'}
                      >
                        {checked && (
                          <svg className="w-3 h-3 text-slate-950" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                            <path d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>

                      {/* Avatar */}
                      {member.foto_path && profileImages[member.mg_nr] && !imageErrors[member.mg_nr] ? (
                        <img
                          src={profileImages[member.mg_nr]}
                          alt={`${member.vorname} ${member.name}`}
                          className={`w-10 h-10 rounded-xl object-cover shrink-0 ${av.className}`}
                          onError={() => setImageErrors(prev => ({ ...prev, [member.mg_nr]: true }))}
                        />
                      ) : (
                        <div
                          className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black shrink-0 ${av.className}`}
                        >
                          {getInitials(member)}
                        </div>
                      )}

                      {/* Name + Subline */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-sm md:text-base font-bold truncate ${isDeleted ? 'line-through text-slate-500' : isInactive ? 'text-gray-500 dark:text-slate-300' : 'text-gray-900 dark:text-white'}`}>
                            {member.vorname} {member.name}
                          </span>
                          <span className="text-[11px] text-gray-500 dark:text-slate-500 tabular-nums">· {memberNr}</span>
                          {isDeleted && member.geloescht_am && (
                            <span className="text-rose-700 dark:text-rose-300 border border-rose-500/40 dark:border-rose-500/30 bg-rose-500/10 dark:bg-rose-500/12" style={pillLayout}>
                              Gelöscht {new Date(member.geloescht_am).toLocaleDateString('de-DE', { month: '2-digit', year: 'numeric' })}
                            </span>
                          )}
                          {!isInactive && lastLogin.label === 'heute' && (
                            <span className="text-emerald-700 dark:text-emerald-300 border border-emerald-500/40 dark:border-emerald-500/30 bg-emerald-500/10 dark:bg-emerald-500/10" style={pillLayout}>
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-primary-400 animate-pulse" />online
                            </span>
                          )}
                          {dogStatus && dogStatus.count > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-primary-300/80 md:hidden">
                              <DogIcon className="h-3 w-3" />
                              {dogStatus.count}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-gray-600 dark:text-slate-400 mt-0.5 truncate">
                          {sublineParts.length > 0 ? sublineParts.join(' · ') : <span className="text-gray-500 dark:text-slate-500">—</span>}
                          {member.tel_nummer && (
                            <span className="hidden md:inline"> · <PhoneIcon className="inline w-3 h-3 -mt-0.5" /> {member.tel_nummer}</span>
                          )}
                          {!isInactive && lastLogin.label !== 'heute' && (
                            <span className={`ml-2 ${lastLogin.color}`}>· zuletzt {lastLogin.label}</span>
                          )}
                        </div>
                      </div>

                      {/* Roles (desktop) */}
                      <div className="hidden md:flex items-center gap-1.5 w-44 flex-wrap">
                        {rolePills.length > 0 ? rolePills.slice(0, 2).map((rp, i) => (
                          <span key={i} className={rolePillClass(rp.tone)} style={pillLayout}>{rp.label}</span>
                        )) : (
                          <span className="text-slate-700 dark:text-slate-300 border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-slate-900/55" style={pillLayout}>—</span>
                        )}
                      </div>

                      {/* Beitrag (desktop) */}
                      <div className="hidden md:block w-32">
                        {isGuest ? (
                          guestCardsCount > 0 ? (
                            <>
                              <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-bold text-amber-700 dark:text-amber-200 border border-amber-500/40 dark:border-amber-500/30 bg-amber-500/10">
                                <TicketIcon className="w-3 h-3" />
                                {guestCardsCount} {guestCardsCount === 1 ? 'Karte' : 'Karten'}
                              </div>
                              <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">
                                {guestCardsRemaining} {guestCardsRemaining === 1 ? 'Training' : 'Trainings'} übrig
                              </div>
                            </>
                          ) : (
                            <span className="text-[11px] text-gray-500 dark:text-slate-500">Keine Karten</span>
                          )
                        ) : isInactive ? (
                          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-bold text-slate-700 dark:text-slate-400 border border-slate-400/40 dark:border-slate-500/30 bg-slate-500/10">
                            ausgesetzt
                          </div>
                        ) : payment && payment.amount > 0 ? (
                          <>
                            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-bold text-emerald-700 dark:text-emerald-300 border border-emerald-500/40 dark:border-emerald-500/30 bg-emerald-500/10">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg>
                              {payment.amount.toFixed(2).replace('.', ',')} €
                            </div>
                            {payment.last_payment_date && (
                              <div className="text-[10px] text-gray-500 dark:text-slate-500 mt-0.5">
                                am {new Date(payment.last_payment_date).toLocaleDateString('de-DE')}
                              </div>
                            )}
                          </>
                        ) : payment && payment.amount < 0 ? (
                          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-bold text-rose-700 dark:text-rose-300 border border-rose-500/40 dark:border-rose-500/30 bg-rose-500/10">
                            {payment.amount.toFixed(2).replace('.', ',')} €
                          </div>
                        ) : (
                          <>
                            <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-bold text-amber-700 dark:text-amber-200 border border-amber-500/40 dark:border-amber-500/30 bg-amber-500/10 dark:bg-amber-500/15">
                              <ExclamationCircleIcon className="w-3 h-3" />
                              offen
                            </div>
                            <div className="text-[10px] text-rose-700 dark:text-rose-300 mt-0.5 font-semibold">noch nicht bezahlt</div>
                          </>
                        )}
                      </div>

                      {/* Eintritt (desktop) */}
                      <div className="hidden md:block w-28">
                        <div className="text-xs font-bold text-gray-900 dark:text-white">
                          {member.eintritt ? new Date(member.eintritt).toLocaleDateString('de-DE') : '—'}
                        </div>
                        {eintrittYears && (
                          <div className="text-[10px] text-gray-600 dark:text-slate-400">{eintrittYears}</div>
                        )}
                      </div>

                      {/* Action (desktop chevron only) */}
                      <div className="hidden md:flex items-center justify-end gap-1 w-20">
                        <ChevronRightIcon className="w-4 h-4 text-gray-500 dark:text-slate-500" />
                      </div>

                      {/* Mobile chevron */}
                      <ChevronRightIcon className="md:hidden w-5 h-5 text-gray-500 dark:text-slate-500 shrink-0" />
                    </div>
                  );
                })}
              </div>

              {/* Mobile: Mehr-laden-Button */}
              {isMobile && mobileVisible < filteredMembers.length && (
                <div className="md:hidden px-5 py-3 border-t border-gray-200 dark:border-slate-800">
                  <button
                    onClick={() => setMobileVisible((v) => v + 25)}
                    className="w-full py-3 rounded-xl bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-800 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-white/10 text-sm font-semibold transition"
                  >
                    Mehr anzeigen ({filteredMembers.length - mobileVisible} weitere)
                  </button>
                </div>
              )}

              {/* List footer */}
              <div className="px-5 py-4 border-t border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-white/[0.015] flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-gray-600 dark:text-slate-400">
                  Zeige{' '}
                  <span className="font-bold text-gray-900 dark:text-white">
                    {isMobile ? Math.min(mobileVisible, filteredMembers.length) : filteredMembers.length}
                  </span>{' '}
                  von <span className="font-bold text-gray-900 dark:text-white">{members.length}</span> {members.length === 1 ? 'Mitglied' : 'Mitgliedern'}
                </div>
                {filteredMembers.length !== members.length && (
                  <button
                    onClick={() => { setSearchTerm(''); setFilterType('all'); }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-800 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-white/10 text-xs font-semibold transition"
                  >
                    Filter zurücksetzen
                  </button>
                )}
              </div>
            </div>
          )}
        </section>

        {/* BULK ACTION BAR */}
        {selectedCount > 0 && (
          <section className="pb-6">
            <div className="rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap bg-white dark:bg-[#1c2432]"
              style={{ border: '1px solid rgba(16,185,129,0.40)', boxShadow: '0 0 0 1px rgba(16,185,129,0.20), 0 12px 40px -8px rgba(16,185,129,0.40)' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.40)', color: '#6ee7b7' }}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg>
                </div>
                <div>
                  <div className="text-sm font-black text-gray-900 dark:text-white">
                    <span className="text-primary-600 dark:text-primary-400 tabular-nums">{selectedCount}</span> {selectedCount === 1 ? 'Mitglied' : 'Mitglieder'} ausgewählt
                  </div>
                  <div className="text-[11px] text-gray-600 dark:text-slate-400 truncate max-w-[40ch]">
                    {selectedMembersList.slice(0, 3).map(m => `${m.vorname} ${m.name.charAt(0)}.`).join(', ')}
                    {selectedMembersList.length > 3 && ` +${selectedMembersList.length - 3} weitere`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => {
                    const emails = selectedMembersList.map(m => m.email).filter(Boolean).join(',');
                    if (emails) window.location.href = `mailto:?bcc=${emails}`;
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10 text-xs font-semibold transition"
                >
                  <EnvelopeIcon className="w-3.5 h-3.5" />
                  E-Mail
                </button>
                <button
                  onClick={() => setShowExcelExportModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10 text-xs font-semibold transition"
                >
                  <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                  Exportieren
                </button>
                <span className="w-px h-6 bg-slate-700 mx-1" />
                <button
                  onClick={clearSelection}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-600 dark:text-slate-400 hover:bg-white/10 text-xs font-semibold transition"
                >
                  Auswahl aufheben
                </button>
              </div>
            </div>
          </section>
        )}
      </div>

      {/* MODALS */}

      {/* Member Detail Modal */}
      {showDetailsModal && selectedMember && (
        <MemberDetail
          member={selectedMember}
          profileImage={profileImages[selectedMember.mg_nr]}
          memberPayments={memberPayments[selectedMember.mg_nr]}
          dogsStatus={dogsStatus[selectedMember.mg_nr]}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedMember(null);
          }}
          onSave={handleMemberSave}
          onDelete={handleMemberDelete}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.isOpen}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: confirmModal.onConfirm
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: confirmModal.onCancel
          },
          confirm: {
            text: 'Bestätigen',
            onPress: confirmModal.onConfirm
          }
        } : undefined}
      />

      {/* CSV Importer Modal */}
      {showCSVImporter && (
        <MembersCSVImporter
          onImportSuccess={() => {
            setShowCSVImporter(false);
            fetchMembers();
          }}
          onClose={() => setShowCSVImporter(false)}
          availableRoles={availableRoles}
        />
      )}

      {/* Audit History Modal */}
      {showAuditModal && (
        <AuditHistoryModal onClose={() => setShowAuditModal(false)} />
      )}

      {/* Excel Export Modal */}
      <MemberExcelExportModal
        isOpen={showExcelExportModal}
        onClose={() => setShowExcelExportModal(false)}
        isMobile={typeof window !== 'undefined' && window.innerWidth < 768}
      />

      {/* New Member Modal */}
      <NewMemberModal
        isOpen={showNewMemberModal}
        onClose={() => {
          setShowNewMemberModal(false);
          setNewMemberData({
            vorname: '',
            name: '',
            email: '',
            tel_nummer: '',
            strasse: '',
            plz: '',
            ort: '',
            geb_am: '',
            gender: '',
            eintritt: '',
            active: 1,
            role_ids: [1] // Reset to default Guest role
          });
        }}
        data={newMemberData}
        setData={setNewMemberData}
        onSubmit={handleCreateMember}
        availableRoles={availableRoles}
      />
    </div>
  );
};

// =============================================================================
// NEW MEMBER MODAL — Dark Story style
// =============================================================================
const NewMemberModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  data: any;
  setData: (data: any) => void;
  onSubmit: () => void;
  availableRoles: any[];
}> = ({ isOpen, onClose, data, setData, onSubmit, availableRoles }) => {
  const [loading, setLoading] = React.useState(false);

  // Body Scroll Lock
  React.useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    await onSubmit();
    setLoading(false);
  };

  if (!isOpen) return null;

  const inputClass = 'mt-1 w-full px-3 py-2.5 rounded-xl text-sm font-bold text-white outline-none transition';
  const inputStyle: React.CSSProperties = {
    background: 'rgba(15,23,42,0.6)',
    border: '1px solid #334155',
    colorScheme: 'dark',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-3xl flex flex-col overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
        style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 32px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between border-b border-slate-800">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">
              Neues Mitglied
            </div>
            <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
              Mitglied anlegen
            </h2>
            <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">
              4 Sektionen · Pflichtfelder mit *
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
            aria-label="Schließen"
          >
            <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Stammdaten */}
            <div className="rounded-2xl p-5 border border-gray-200 dark:border-slate-700" style={{ background: 'rgba(15,23,42,0.30)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}>
                  <UserIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Stammdaten</div>
                  <h5 className="text-base font-black text-gray-900 dark:text-white">Wer das ist</h5>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Vorname *</span>
                  <input
                    type="text"
                    name="vorname"
                    value={data.vorname}
                    onChange={handleInputChange}
                    required
                    className={inputClass}
                    style={inputStyle}
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Nachname *</span>
                  <input
                    type="text"
                    name="name"
                    value={data.name}
                    onChange={handleInputChange}
                    required
                    className={inputClass}
                    style={inputStyle}
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Geburtsdatum</span>
                  <input
                    type="date"
                    name="geb_am"
                    value={data.geb_am}
                    onChange={handleInputChange}
                    className={inputClass}
                    style={inputStyle}
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Geschlecht</span>
                  <select
                    name="gender"
                    value={data.gender ?? ''}
                    onChange={handleInputChange}
                    className={inputClass}
                    style={inputStyle}
                  >
                    <option value="">Nicht angeben</option>
                    <option value="0">Männlich</option>
                    <option value="1">Weiblich</option>
                  </select>
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">
                    Eintrittsdatum
                    {data.role_ids?.length === 1 && data.role_ids[0] === 1 && (
                      <span className="ml-2 text-gray-500 dark:text-slate-500 normal-case tracking-normal">(optional für Gäste)</span>
                    )}
                  </span>
                  <input
                    type="date"
                    name="eintritt"
                    value={data.eintritt || ''}
                    onChange={handleInputChange}
                    className={inputClass}
                    style={inputStyle}
                  />
                </label>
              </div>
            </div>

            {/* Rollen */}
            <div className="rounded-2xl p-5 border border-gray-200 dark:border-slate-700" style={{ background: 'rgba(15,23,42,0.30)' }}>
              <div className="flex items-start justify-between mb-4 gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}>
                    <ShieldCheckIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Rollen</div>
                    <h5 className="text-base font-black text-gray-900 dark:text-white">Welche Rechte er hat</h5>
                  </div>
                </div>
                <span style={{ ...pillStyleStatic, color: '#cbd5e1' }}>Mehrfachauswahl</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {availableRoles.map((role) => {
                  const isChecked = data.role_ids?.includes(role.id) || false;
                  let toneBg = 'rgba(15,23,42,0.40)';
                  let toneBorder = '#334155';
                  let toneText = '#ffffff';
                  let subText = '#94a3b8';
                  if (isChecked) {
                    switch (role.name) {
                      case 'board':
                        toneBg = 'rgba(245,158,11,0.10)'; toneBorder = 'rgba(245,158,11,0.40)'; toneText = '#fde68a'; subText = '#fbbf24';
                        break;
                      case 'trainer':
                        toneBg = 'rgba(59,130,246,0.10)'; toneBorder = 'rgba(59,130,246,0.40)'; toneText = '#bfdbfe'; subText = '#93c5fd';
                        break;
                      case 'auditor':
                        toneBg = 'rgba(245,158,11,0.10)'; toneBorder = 'rgba(245,158,11,0.40)'; toneText = '#fde68a'; subText = '#fbbf24';
                        break;
                      case 'member':
                        toneBg = 'rgba(16,185,129,0.10)'; toneBorder = 'rgba(16,185,129,0.40)'; toneText = '#a7f3d0'; subText = '#6ee7b7';
                        break;
                      case 'guest':
                        toneBg = 'rgba(148,163,184,0.10)'; toneBorder = 'rgba(148,163,184,0.40)'; toneText = '#e2e8f0'; subText = '#cbd5e1';
                        break;
                    }
                  }
                  const subLabel =
                    role.name === 'guest' ? 'Standard' :
                    role.name === 'member' ? 'Vollmitglied' :
                    role.name === 'trainer' ? 'Trainer-Rechte' :
                    role.name === 'board' ? 'Vorstand' :
                    role.name === 'auditor' ? 'Kassenprüfer' : '';
                  return (
                    <label
                      key={role.id}
                      className="flex items-center gap-2 px-3 py-3 rounded-xl cursor-pointer transition"
                      style={{ background: toneBg, border: `2px solid ${toneBorder}` }}
                    >
                      <div
                        className="shrink-0 flex items-center justify-center"
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 6,
                          border: isChecked ? '1.5px solid #10b981' : '1.5px solid rgba(148,163,184,0.5)',
                          background: isChecked ? '#10b981' : 'transparent',
                        }}
                      >
                        {isChecked && (
                          <svg className="w-3 h-3 text-slate-950" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                            <path d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {
                          const currentRoles = data.role_ids || [];
                          if (currentRoles.includes(role.id)) {
                            setData({ ...data, role_ids: currentRoles.filter((id: number) => id !== role.id) });
                          } else {
                            setData({ ...data, role_ids: [...currentRoles, role.id] });
                          }
                        }}
                        className="hidden"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-black truncate" style={{ color: toneText }}>{role.description}</div>
                        {subLabel && <div className="text-[10px]" style={{ color: subText }}>{subLabel}</div>}
                      </div>
                    </label>
                  );
                })}
              </div>
              {(!data.role_ids || data.role_ids.length === 0) && (
                <div className="mt-3 p-3 rounded-xl text-[12px] text-amber-200"
                  style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}>
                  Keine Rolle ausgewählt — Standard wird „Gast" sein.
                </div>
              )}
            </div>

            {/* Kontakt */}
            <div className="rounded-2xl p-5 border border-gray-200 dark:border-slate-700" style={{ background: 'rgba(15,23,42,0.30)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}>
                  <EnvelopeIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Kontakt</div>
                  <h5 className="text-base font-black text-gray-900 dark:text-white">So erreicht ihr ihn</h5>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block sm:col-span-2">
                  <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">E-Mail · Login</span>
                  <input
                    type="email"
                    name="email"
                    value={data.email}
                    onChange={handleInputChange}
                    className={inputClass}
                    style={inputStyle}
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Telefon</span>
                  <input
                    type="tel"
                    name="tel_nummer"
                    value={data.tel_nummer}
                    onChange={handleInputChange}
                    className={inputClass}
                    style={inputStyle}
                  />
                </label>
              </div>
            </div>

            {/* Adresse */}
            <div className="rounded-2xl p-5 border border-gray-200 dark:border-slate-700" style={{ background: 'rgba(15,23,42,0.30)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                  style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}>
                  <MapPinIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Adresse</div>
                  <h5 className="text-base font-black text-gray-900 dark:text-white">Wo er wohnt</h5>
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <label className="block sm:col-span-3">
                  <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">
                    <HomeIcon className="inline w-3 h-3 -mt-0.5 mr-1" />
                    Straße
                  </span>
                  <input
                    type="text"
                    name="strasse"
                    value={data.strasse}
                    onChange={handleInputChange}
                    className={inputClass}
                    style={inputStyle}
                  />
                </label>
                <label className="block">
                  <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">PLZ</span>
                  <input
                    type="text"
                    name="plz"
                    value={data.plz}
                    onChange={handleInputChange}
                    className={inputClass}
                    style={inputStyle}
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Ort</span>
                  <input
                    type="text"
                    name="ort"
                    value={data.ort}
                    onChange={handleInputChange}
                    className={inputClass}
                    style={inputStyle}
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 flex items-center justify-end gap-2 flex-wrap"
            style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10 text-xs font-semibold transition"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading || !data.vorname || !data.name}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 text-xs font-bold transition"
            >
              {!loading && <UserPlusIcon className="w-4 h-4" />}
              {loading ? 'Speichern…' : 'Mitglied hinzufügen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Static pill style used in modal
const pillStyleStatic: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  borderRadius: 9999,
  fontSize: 11,
  fontWeight: 600,
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'rgba(2,6,23,0.55)',
};

export default Members;
